import { CalendarDays, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FieldRow } from '@/components/custom-ui/FieldRow'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { User } from '@/services/api'
import { intlLocale } from '@/services/i18n/languages'
import { ProfileUnitField, type ProfileUnitFieldProps } from './ProfileUnitField'

export interface ProfileSectionProps {
  user: User
  /** The editable operator unit; the caller passes it only while HUMINT is on (ADR-0051). */
  unit?: ProfileUnitFieldProps
}

export function ProfileSection({ user, unit }: ProfileSectionProps) {
  const { t, i18n } = useTranslation()

  const joinedDate = new Date(user.created_at).toLocaleDateString(intlLocale(i18n.language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.profile.title')}</CardTitle>
        <CardDescription>{t('settings.profile.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <FieldRow label={t('settings.profile.username')}>{user.username}</FieldRow>
          <FieldRow label={t('settings.profile.status')}>
            <span className={user.is_active ? 'text-success' : 'text-destructive'}>
              {user.is_active ? t('settings.profile.active') : t('settings.profile.inactive')}
            </span>
          </FieldRow>
          <FieldRow label={t('settings.profile.fullName')}>
            {user.full_name || <span className="text-muted-foreground/50">—</span>}
          </FieldRow>
          <FieldRow label={t('settings.profile.role')}>
            {user.is_superuser ? (
              <span className="text-primary">{t('settings.profile.superuser')}</span>
            ) : (
              <span className="text-muted-foreground">{t('settings.profile.standardUser')}</span>
            )}
          </FieldRow>
          {user.email && (
            <FieldRow label={t('settings.profile.email')}>
              <span className="flex items-center gap-1.5">
                <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="min-w-0 break-all">{user.email}</span>
              </span>
            </FieldRow>
          )}
          <FieldRow label={t('settings.profile.memberSince')}>
            <span className="flex items-center gap-1.5">
              <CalendarDays className="h-3 w-3 text-muted-foreground" />
              {joinedDate}
            </span>
          </FieldRow>
          {unit && <ProfileUnitField key={unit.value ?? ''} {...unit} />}
        </div>
      </CardContent>
    </Card>
  )
}
